import { useState } from "react";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { QrCode, Loader2 } from "lucide-react";
import { formatError } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(username.trim(), password);
      toast.success("Selamat datang kembali");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-stretch bg-slate-50 dark:bg-[#0B0F17]" data-testid="login-page">
      {/* Left brand panel */}
      <div className="hidden md:flex flex-1 relative overflow-hidden bg-[#0B0F17] text-white subtle-grid">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-600/20 via-transparent to-cyan-500/10" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-sky-500 flex items-center justify-center shadow-lg shadow-sky-500/30">
              <QrCode className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <div className="jscan-brand text-2xl">J-scan</div>
              <div className="text-xs uppercase tracking-widest text-sky-300/80">Admin Console</div>
            </div>
          </div>

          <div className="space-y-6 max-w-md">
            <h1 className="jscan-brand text-4xl leading-tight">
              QR akrilik yang <span className="text-sky-400">tidak pernah usang.</span>
            </h1>
            <p className="text-slate-300 text-base leading-relaxed">
              Cetak sekali, ganti tujuan review kapan saja. Kelola pesanan UMKM dari satu dashboard.
            </p>
            <div className="grid grid-cols-3 gap-3 pt-4">
              {[
                { k: "Batch", v: "50/klik" },
                { k: "Kunci", v: "Reversible" },
                { k: "PNG", v: "Cetak siap" },
              ].map((it) => (
                <div key={it.k} className="rounded-lg border border-white/10 bg-white/5 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">{it.k}</div>
                  <div className="jscan-mono text-sm text-sky-300 mt-1">{it.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-xs text-slate-500 jscan-mono">© 2026 J-scan · Internal Tools</div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <form onSubmit={submit} className="w-full max-w-sm fade-in" data-testid="login-form">
          <div className="md:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-sky-500 flex items-center justify-center">
              <QrCode className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="jscan-brand text-xl">J-scan</div>
          </div>

          <h2 className="jscan-brand text-3xl mb-2">Masuk Admin</h2>
          <p className="text-sm text-muted-foreground mb-8">
            Hanya untuk tim internal J-scan.
          </p>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                data-testid="login-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin"
                autoComplete="username"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 rounded-full bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold shadow-md shadow-sky-500/20"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Memuat...
                </>
              ) : (
                "Masuk"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
